import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewChild, ElementRef, DestroyRef, inject, Injector } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { slideDownUp } from '../../shared/animations';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgClass } from '@angular/common';
import { MenuModule } from 'headlessui-angular';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthService } from 'src/app/auth/auth.service';
import { ImageService } from 'src/app/core/service/image.service';
import { UserService } from 'src/app/pages/vendor/services/user.service';
import { UserProfileStateService } from 'src/app/core/service/user-profile-state.service';
import { environment } from 'src/environments/environment';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, switchMap } from 'rxjs';
import { PermissionService } from '@app/core/service/permission.service';

@Component({
    selector: 'sidebar',
    templateUrl: './sidebar.html',
    animations: [
        slideDownUp,
        trigger('toggleAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))]),
        ]),
    ],
    standalone: true,
    imports: [NgClass, RouterLink, RouterLinkActive, NgScrollbarModule, TranslateModule, MenuModule, SharedModule],
})
export class SidebarComponent implements OnInit, OnDestroy {
    active = false;
    store: any;
    activeDropdown: string[] = [];
    parentDropdown: string = '';
    @ViewChild('sidebarScroll') sidebarScroll!: ElementRef;
    @ViewChild('mastersDropdown') mastersDropdown!: ElementRef;
    profile_picture: string = '';
    VendorName: string = '';
    userData: any = {};
    userProfile = this.userProfileState.userProfile;
    userProfileImageUrl = '';
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        private readonly authService: AuthService,
        public imageService: ImageService,
        private readonly userProfileState: UserProfileStateService,
        public permissionService: PermissionService
    ) {}

    ngOnInit() {
        this.initStore();
        this.setActiveDropdown();
        // Subscribe to route changes - but don't auto-open dropdowns
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                // Route changed - parent menu items will show as active via isPurchaseActive() / isAdminMasterActive()
                // But don't auto-open dropdowns
            }
        });
        // close flyout when clicking outside
        document.addEventListener('click', this.handleDocumentClick, true);

        this.userData = this.authService.getVendorData();
        this.watchUserProfileForAvatar();
    }

    private watchUserProfileForAvatar(): void {
        toObservable(this.userProfile, { injector: this.injector })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map((p) => p ?? this.authService.getVendorData()),
                distinctUntilChanged((a, b) => a?.id === b?.id && JSON.stringify(a?.profile_picture ?? null) === JSON.stringify(b?.profile_picture ?? null)),
                switchMap((u) => {
                    this.userData = { ...u };
                    return this.imageService.resolveSuperAdminUserProfileImageUrl(u);
                })
            )
            .subscribe({
                next: (url) => {
                    this.userProfileImageUrl = url;
                },
                error: (error) => {
                    console.error('Error loading profile image:', error);
                    this.userProfileImageUrl = '';
                },
            });
    }
    initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }
    setActiveMenuBasedOnRoute() {
        // Don't auto-open dropdowns on route change
        // Only update activeDropdown if user manually opened it
        // The parent menu items will still show as active via isPurchaseActive() / isAdminMasterActive()
        // This allows the menu item to be highlighted while keeping dropdown closed
    }

    isPurchaseActive(): boolean {
        return this.router.url.startsWith('/purchase-master');
    }

    isAdminMasterActive(): boolean {
        return this.router.url.startsWith('/master');
    }

    setActiveDropdown() {
        const selector = document.querySelector('.sidebar ul a[routerLink="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                // Previously this auto-clicked the parent nav-link to expand the accordion on load.
                // Removed to prevent auto-opening menus when the page reloads.
            }
        }
    }

    toggleMobileMenu() {
        if (window.innerWidth < 1024) {
            this.storeData.dispatch({ type: 'toggleSidebar' });
        }
    }

    closeDropdownOnSubmenuClick() {
        // Close all accordions immediately when a submenu item is clicked
        // This ensures dropdown closes right away, before navigation happens
        this.activeDropdown = [];

        // Also close any open flyouts (for collapsed sidebar)
        this.closeAllFlyouts();

        // Prevent auto-opening after navigation by clearing dropdowns again after a short delay
        setTimeout(() => {
            this.activeDropdown = [];
            this.closeAllFlyouts();
        }, 100);
    }

    toggleAccordion(name: string, parent?: string) {
        if (this.activeDropdown.includes(name)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
        } else {
            // Close other accordions when opening a new one (only for top-level accordions)
            if (name === 'Purchase' || name === 'AdminMaster') {
                this.activeDropdown = this.activeDropdown.filter((d) => d !== 'Purchase' && d !== 'AdminMaster');
            }
            this.activeDropdown.push(name);
        }
    }

    toggleNestedAccordion(name: string, event?: MouseEvent) {
        // First, ensure parent 'AdminMaster' is open
        const wasParentClosed = !this.activeDropdown.includes('AdminMaster');
        if (wasParentClosed) {
            this.activeDropdown.push('AdminMaster');
        }

        // Then toggle the nested accordion
        const isOpening = !this.activeDropdown.includes(name);

        if (this.activeDropdown.includes(name)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
            // Remove upward shift class from parent when closing nested menu
            // this.removeParentShiftClass();
        } else {
            this.activeDropdown.push(name);
        }

        // Check if we need to expand upward and shift parent (only for default sidebar, not vertical)
        if (isOpening && event && !this.isVerticalSidebar) {
            const delay = wasParentClosed ? 600 : 350;
            setTimeout(() => {
                this.checkNestedDropdownPosition(event.target as HTMLElement);
            }, delay);
        }
    }

    private checkNestedDropdownPosition(element: HTMLElement) {
        // Only run this logic for default sidebar, not vertical sidebar
        if (this.isVerticalSidebar) {
            return;
        }

        if (!this.sidebarScroll) return;

        const liElement = element.closest('li.nested-accordion-wrapper');

        if (!liElement) return;

        // Position checking logic can be added here if needed
    }

    get isCollapsed(): boolean {
        const nav = document.querySelector('nav.sidebar');
        return !!nav?.closest('.collapsed-default');
    }

    get isVerticalSidebar(): boolean {
        const body = document.body;
        return body.classList.contains('vertical') || body.classList.contains('collapsible-vertical');
    }

    onNavClick(name: string, $event: MouseEvent) {
        const target = $event.target as HTMLElement;
        const sidebarEl = target.closest('nav.sidebar');
        const isCollapsed = !!sidebarEl?.closest('.collapsed-default');

        if (isCollapsed) {
            // Prevent default accordion behavior; open flyout instead
            $event.preventDefault();
            $event.stopPropagation();

            const li = target.closest('li.menu.nav-item');
            if (!li) return;

            // close other flyouts first
            this.closeAllFlyouts(sidebarEl as HTMLElement);

            // toggle this one
            li.classList.toggle('open-flyout');
            return;
        }

        // Check if this is a direct menu item (not an accordion)
        const button = target.closest('button');
        const hasRouterLink = button?.hasAttribute('routerLink');

        // If it's a direct menu item with routerLink, close all accordions
        if (hasRouterLink && name !== 'Purchase' && name !== 'AdminMaster') {
            // Close all accordions when clicking on direct menu items
            this.activeDropdown = [];
            return;
        }

        // Not collapsed: behave as accordion
        // Close other accordions first, then toggle this one
        if (name === 'Purchase' || name === 'AdminMaster') {
            // Prevent default behavior for accordion buttons
            $event.preventDefault();
            $event.stopPropagation();

            // Close other accordions
            if (name === 'Purchase') {
                this.activeDropdown = this.activeDropdown.filter((d) => d !== 'AdminMaster');
            } else if (name === 'AdminMaster') {
                this.activeDropdown = this.activeDropdown.filter((d) => d !== 'Purchase');
            }
            // Toggle this accordion
            this.toggleAccordion(name);
        } else {
            // For non-accordion items, just close all accordions
            this.activeDropdown = [];
        }
    }

    private closeAllFlyouts(scope?: HTMLElement | Document) {
        const root: HTMLElement | Document = scope || document;
        const openItems = root.querySelectorAll('li.menu.nav-item.open-flyout');
        openItems.forEach((el) => el.classList.remove('open-flyout'));
    }

    private readonly handleDocumentClick = (ev: Event) => {
        const t = ev.target as HTMLElement;
        const anyOpen = document.querySelector('li.menu.nav-item.open-flyout');
        if (!anyOpen) return;

        // if click is inside an open item or its flyout, keep it open
        const openLi = anyOpen as HTMLElement;
        if (openLi.contains(t)) return;

        // otherwise close all
        this.closeAllFlyouts();
    };

    signOut() {
        // Sign out logic can be added here if needed
        this.authService.logout();
        this.router.navigate(['/auth/signin']);
    }

    navigateToTenant() {
        this.router.navigate(['/tenant']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }

    navigateToBuyerManagement() {
        this.router.navigate(['/buyer-master/buyer-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    navigateToVendorManagement() {
        this.router.navigate(['/vendor-master/vendor-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    navigateToVendorVerificationKYC() {
        this.router.navigate(['/vendor-kyc-master/vendor-kyc-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    navigateToTeamManagement() {
        this.router.navigate(['/team-master/user-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    navigateToSupport() {
        this.router.navigate(['/support-master/support-helpdesk-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    navigateToDepartmentManagement() {
        this.router.navigate(['/department-master/department-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    navigateToCategoryManagement() {
        this.router.navigate(['/category-master/category-list']).catch((error) => {
            console.error('Navigation error:', error);
        });
    }
    ngOnDestroy(): void {
        document.removeEventListener('click', this.handleDocumentClick, true);
    }
}
